/**
 * Centro Owner — FASE 10 aggregates: agents, packs, playbooks, goals, collaboration, ROI hints.
 */
import { createFileRoute } from "@tanstack/react-router";
import { Crown, Users, Boxes, BookOpen, Flag, Network, TrendingUp } from "lucide-react";
import { listAgents } from "@/lib/renata/agents/registry";
import { listExpertisePacks } from "@/lib/renata/packs/registry";
import { listPlaybooks } from "@/lib/renata/playbooks/registry";
import { listGoals } from "@/lib/renata/goals/registry";
import { listCollaborationEdges } from "@/lib/renata/collaboration/graph";
import { listMissionTemplates } from "@/lib/renata/missions/registry";
import { listTaskDefinitions } from "@/lib/renata/tasks/registry";
import { InlineExecutiveAi } from "@/components/renata/InlineExecutiveAi";

export const Route = createFileRoute("/admin/ops/ai/owner-center")({
  component: OwnerAiCenterPage,
  head: () => ({ meta: [{ title: "Centro Owner — Admin" }] }),
});

function OwnerAiCenterPage() {
  const agents = listAgents();
  const packs = listExpertisePacks();
  const playbooks = listPlaybooks();
  const goals = listGoals();
  const edges = listCollaborationEdges();
  const missions = listMissionTemplates();
  const tasks = listTaskDefinitions();

  const topAgents = [...agents].sort((a, b) => b.priority - a.priority).slice(0, 5);
  const topPacks = packs.slice(0, 5);
  const topPlaybooks = [...playbooks].sort((a, b) => b.priority - a.priority).slice(0, 5);
  const topGoals = goals.slice(0, 5);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Crown className="h-5 w-5 text-amber-500" /> Centro Owner
        </h1>
        <p className="text-sm text-muted-foreground">
          Vista owner: agentes, packs, playbooks, goals, colaboración y éxito estimado (declarativo).
        </p>
        <InlineExecutiveAi />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Agents" value={agents.length} icon={Users} />
        <Stat label="Expertise Packs" value={packs.length} icon={Boxes} />
        <Stat label="Guías de trabajo" value={playbooks.length} icon={BookOpen} />
        <Stat label="Objetivos" value={goals.length} icon={Flag} />
        <Stat label="Collab edges" value={edges.length} icon={Network} />
        <Stat label="Missions" value={missions.length} icon={TrendingUp} />
        <Stat label="Acciones" value={tasks.length} icon={TrendingUp} />
        <Stat label="Flujo" value="Borrador→Aprobación" icon={TrendingUp} />
      </div>

      <Section title="Top Agents" items={topAgents.map((a) => `${a.name} (p${a.priority})`)} />
      <Section title="Top Expertise Packs" items={topPacks.map((p) => p.name)} />
      <Section title="Mejores guías" items={topPlaybooks.map((p) => `${p.title} (p${p.priority})`)} />
      <Section title="Objetivos destacados" items={topGoals.map((g) => g.title)} />

      <div className="rounded-xl border p-4 space-y-2 border-emerald-300/60">
        <h2 className="text-sm font-semibold">Tienda de extensiones</h2>
        <p className="text-xs text-muted-foreground">
          Extensiones declarativas · SDK · Analytics · Permisos · Health. Tenant: solo Mis extensiones (Store).
        </p>
        <a className="text-xs underline" href="/admin/ops/ai/marketplace">Abrir tienda</a>
      </div>

      <div className="rounded-xl border p-4 space-y-2 border-violet-300/60">
        <h2 className="text-sm font-semibold">AI Studio (FASE 14 P3)</h2>
        <p className="text-xs text-muted-foreground">
          Prompt Registry · Versioning / Diff / Rollback · Sandbox · Benchmark · Cost Simulator · A/B · Prompt Score.
          Solo Owner SaaS. Tenant solo ve consumo.
        </p>
        <a className="text-xs underline" href="/admin/ops/ai/studio">Abrir AI Studio</a>
      </div>

      <div className="rounded-xl border p-4 space-y-2">
        <h2 className="text-sm font-semibold">Biblioteca (FASE 12)</h2>
        <p className="text-xs text-muted-foreground">
          Layers · Embeddings · Vector (pgvector) · Hybrid Search · RAG · Citations · Health.
          Agente de biblioteca. Aprendizajes supervisados.
        </p>
        <a className="text-xs underline" href="/app/renata/knowledge">Abrir Biblioteca</a>
      </div>

      <div className="rounded-xl border p-4 space-y-2">
        <h2 className="text-sm font-semibold">Business Capture (FASE 11)</h2>
        <p className="text-xs text-muted-foreground">
          Inbox unificado · OCR · Vision · Confidence · Errores · Clasificaciones · Propuestas.
          Vision Agent colabora con Catalog / Inventory / Purchasing / Marketing. Sin RAG.
        </p>
        <a className="text-xs underline" href="/app/renata/inbox">Abrir Business Inbox</a>
      </div>

      <div className="rounded-xl border p-4 space-y-2">
        <h2 className="text-sm font-semibold">Éxito de planificador / planes / acciones</h2>
        <p className="text-xs text-muted-foreground">
          Éxito = drafts aprobados vía ApprovalCenter + progreso de misiones/tasks existentes.
          No hay ejecución autónoma. ROI estimado vive en playbooks (estimatedRevenue/Cost).
        </p>
        <div className="flex flex-wrap gap-2 text-xs">
          <a className="underline" href="/admin/ops/ai/agents">Agents</a>
          <a className="underline" href="/admin/ops/ai/missions">Missions</a>
          <a className="underline" href="/admin/ops/ai/tasks">Acciones</a>
          <a className="underline" href="/admin/ops/ai/insights">Oportunidades</a>
        </div>
      
      <div className="rounded-xl border p-4 space-y-2">
        <h2 className="text-sm font-semibold">Aprendizajes (FASE 13)</h2>
        <p className="text-xs text-muted-foreground">
          Candidates · Patterns · Rules · Experiences · Decay · Quality. Nunca auto-aplica — Approval humano.
        </p>
        <a className="text-xs underline" href="/app/renata/learning">Abrir Aprendizajes</a>
        </div>

      <div className="rounded-xl border p-4 space-y-2">
        <h2 className="text-sm font-semibold">Automatizaciones (FASE 14 P2)</h2>
        <p className="text-xs text-muted-foreground">
          Catálogo unificado (Growth + Cron + plantillas). Simulación y Approval. Sin auto-ejecución. Notification Hub.
        </p>
        <a className="text-xs underline" href="/app/renata/automatizaciones">Abrir Automatizaciones</a>
      </div>

        <div className="hidden">
      </div>

</div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
    </div>
  );
}

function Section({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-xl border p-4">
      <h2 className="text-sm font-semibold mb-2">{title}</h2>
      <ul className="text-sm space-y-1">
        {items.map((it) => (
          <li key={it} className="text-muted-foreground">• {it}</li>
        ))}
      </ul>
    </div>
  );
}
